export class BufferingAbortedError extends Error {
    override name = 'BufferingAbortedError';
    classType: string;
    parentIndex: number | undefined;

    constructor(classType: string, parentIndex: number | undefined, reason: string) {
        super('Aborted buffering because ' + reason);
        // Re-anchor prototype: defends against runtime Error wrappers (extensions,
        // third-party scripts) that break super()'s new.target semantics.
        Object.setPrototypeOf(this, BufferingAbortedError.prototype);
        this.classType = classType;
        this.parentIndex = parentIndex;
    }
}

export class FailedToLoadError extends Error {
    #metadata = {};

    constructor(message: string, metadata = {}) {
        super(message);
        Object.setPrototypeOf(this, FailedToLoadError.prototype);
        this.#metadata = metadata;
    }

    get metadata() {
        return this.#metadata;
    }
}

/**
 * Raised on the promise of a render task the queue gave up on — the queue was cleared or
 * destroyed, the task's scene went away, or the caller aborted it. The render did not fail;
 * nobody is waiting for its output any more, so consumers should settle quietly rather than
 * report this.
 */
export class RenderTaskDroppedError extends Error {
    override name = 'RenderTaskDroppedError';

    constructor(reason: string) {
        super('Render task dropped because ' + reason);
        Object.setPrototypeOf(this, RenderTaskDroppedError.prototype);
    }
}

/**
 * Raised when the browser refuses to upload a media's pixels because its source came from another
 * origin without CORS approval. The source URL goes to the `mediaSourceTaintsCanvas` reporter
 * context, so every such media groups into one issue.
 */
export class MediaSourceTaintsCanvasError extends Error {
    override name = 'MediaSourceTaintsCanvasError';

    constructor(options?: ErrorOptions) {
        super('Media source taints the canvas it is drawn into', options);
        Object.setPrototypeOf(this, MediaSourceTaintsCanvasError.prototype);
    }
}

/**
 * Reported when a video stops loading part way through, typically because the server answered a
 * later byte range with an error. The browser treats such a video as cross-origin from then on, so
 * drawing it would taint the canvas; the asset stops drawing it instead.
 */
export class MediaFailedToLoadError extends Error {
    override name = 'MediaFailedToLoadError';

    constructor() {
        super('Media failed to load');
        Object.setPrototypeOf(this, MediaFailedToLoadError.prototype);
    }
}

/**
 * Whether a texture upload failed because the canvas holds cross-origin pixels. The taint is
 * permanent: clearing the canvas does not lift it.
 */
export function isTaintedCanvasError(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'SecurityError';
}
